/* Common UI primitives */
import { X } from "lucide-react";

export function Badge({ tone = "slate", dot, children, testId }) {
  return (
    <span
      data-testid={testId}
      className={`badge badge-${tone}${dot ? " badge-dot" : ""}`}
    >
      {children}
    </span>
  );
}

const STATUS_TONES = {
  Draft: "slate",
  Bidding: "amber",
  Sent: "blue",
  Negotiating: "violet",
  Won: "emerald",
  Lost: "rose",
  Cancelled: "slate",
  Expired: "slate",
};

export function BidStatus({ status }) {
  return (
    <Badge tone={STATUS_TONES[status] || "slate"} dot testId={`status-${status}`}>
      {status}
    </Badge>
  );
}

const DMS_TONES = {
  draft: "slate",
  issued: "green",
  sent: "blue",
  accepted: "emerald",
  completed: "emerald",
  rejected: "rose",
  cancelled: "slate",
};

export function DMSStatus({ status }) {
  return (
    <Badge tone={DMS_TONES[status] || "slate"} dot testId={`dmsstatus-${status}`}>
      {status}
    </Badge>
  );
}

export function PageHeader({ title, subtitle, action, icon: Icon }) {
  return (
    <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
      <div className="flex items-start gap-3">
        {Icon && (
          <div className="empty-icon" style={{ width: 44, height: 44, marginBottom: 0 }}>
            <Icon size={20} />
          </div>
        )}
        <div>
          <h1 className="h1">{title}</h1>
          {subtitle && <p className="muted text-sm mt-1">{subtitle}</p>}
        </div>
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

export function SlideOver({ open, onClose, title, subtitle, children, footer, narrow }) {
  if (!open) return null;
  return (
    <>
      <div className="slideover-backdrop" onClick={onClose} data-testid="slideover-backdrop" />
      <div className={`slideover ${narrow ? "slideover-narrow" : ""}`} data-testid="slideover">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <div className="h2">{title}</div>
            {subtitle && <div className="text-xs muted mt-0.5">{subtitle}</div>}
          </div>
          <button
            onClick={onClose}
            data-testid="slideover-close"
            className="text-slate-400 hover:text-slate-700 p-1"
          >
            <X size={20} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer && (
          <div className="px-6 py-4 border-t border-slate-100 flex justify-start gap-2 flex-row-reverse" style={{ paddingRight: "190px" }}>
            {footer}
          </div>
        )}
      </div>
    </>
  );
}

export function Empty({ icon: Icon, title, description, action }) {
  return (
    <div className="empty">
      {Icon && (
        <div className="empty-icon">
          <Icon size={24} />
        </div>
      )}
      <div className="h3" style={{ color: "var(--ink-900)" }}>
        {title}
      </div>
      {description && (
        <div className="text-sm muted mt-1 max-w-md mx-auto">{description}</div>
      )}
      {action && <div className="mt-4 inline-flex">{action}</div>}
    </div>
  );
}

export function Spinner({ size = 16 }) {
  return (
    <span
      className="spinner"
      style={{ width: size, height: size, display: "inline-block" }}
    />
  );
}

export function Field({ label, hint, children }) {
  return (
    <div>
      <label className="label">{label}</label>
      {children}
      {hint && <div className="text-[11px] muted mt-1">{hint}</div>}
    </div>
  );
}
